import React, { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { getCompanyUsers } from "../../../services/axios/AuthApis/TeamCollabApis";

const AssignSelectedControlsModal = ({
    isOpen,
    onClose,
    selectedControls,
    onRemoveControl,
    onAssign,
}) => {
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [dueDate, setDueDate] = useState("");
    const [note, setNote] = useState("");
    
    const [loading, setLoading] = useState(false);
    const [searching, setSearching] = useState(false);
    const [employeeOptions, setEmployeeOptions] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const userDetail = useSelector((state) => state.user);
    let customerMasterId = null;
    if (userDetail?.user?.role === 'customer_admin') {
        customerMasterId = userDetail?.user?.customer_master_id;
    } else if (userDetail?.user?.role == 'security_analyst') { 
        const selectedCompany = useSelector((state) => state.company.selectedCompany);
        customerMasterId = selectedCompany?.company_id;
    }
    const debounceTimeout = useRef(null);
    useEffect(() => {
        if (!isOpen || selectedControls.length === 0) return;

        const dueDates = selectedControls.map(ctrl => ctrl.due_date || null);

        const assignees = selectedControls.map(ctrl => {
            return ctrl.assign_details?.[0]?.user_details || null;
        });

        const allSameDueDate = dueDates.every(d => d === dueDates[0]);
        const allSameAssignee = assignees.every(a => a?.user_id === assignees[0]?.user_id);
        if (allSameDueDate && dueDates[0]) {
            setDueDate(dueDates[0].split("T")[0]);
        } else {
            setDueDate("");
        }
        if (allSameAssignee && assignees[0]) {
            setSelectedEmployee(assignees[0]);
            setSearchTerm(`${assignees[0].name}`);
        } else {
            setSelectedEmployee(null);
            setSearchTerm("");
        }
        let message = "";
        if (!allSameDueDate && !allSameAssignee) {
            message = "Selected controls have different due dates and assignees.";
        } else if (!allSameDueDate) {
            message = "Selected controls have different due dates.";
        } else if (!allSameAssignee) {
            message = "Selected controls have different assignees.";
        }

        setNote(message);

    }, [selectedControls, isOpen]);

    const fetchEmployees = useCallback(
        async (term = "", page = 1) => {
            if (!customerMasterId) return;
            setSearching(true);
            try {
                const response = await getCompanyUsers(customerMasterId, page, 20, term);
                setEmployeeOptions(response?.results || []);
            } catch (error) {
                console.error("Error fetching employees:", error);
                setEmployeeOptions([]);
            } finally {
                setSearching(false);
            }
        },
        [customerMasterId]
    );

    useEffect(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        if (!isOpen) return;

        if (searchTerm.trim() === "") {
            setEmployeeOptions([]);
            return;
        }

        debounceTimeout.current = setTimeout(() => {
            fetchEmployees(searchTerm);
        }, 300);

        return () => clearTimeout(debounceTimeout.current);
    }, [searchTerm, fetchEmployees, isOpen]);

    useEffect(() => {
        if (!isOpen) {
            setSelectedEmployee(null);
            setDueDate("");
            setSearchTerm("");
            setEmployeeOptions([]);
            setIsDropdownOpen(false);
            setNote("");
        }
    }, [isOpen]);

    const handleSelectEmployee = (employee) => {
        setSelectedEmployee(employee);
        setSearchTerm(`${employee.firstname} ${employee.lastname}`);
        setIsDropdownOpen(false);
    };

    const clearSelection = () => {
        setSelectedEmployee(null);
        setSearchTerm("");
        setIsDropdownOpen(false);
        setEmployeeOptions([]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedControls.length) return;

        setLoading(true);
        try {
            await onAssign({
                controlIds: selectedControls.map((control) => control.id),
                controls: selectedControls,
                employeeId: selectedEmployee?.employee_id,
                dueDate: dueDate || null,
            });
            onClose();
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-lg w-full max-w-xl">
                <div className="p-4 border-b flex justify-between items-center">
                    <h3 className="font-semibold text-lg">Assign Selected Controls</h3>
                    <button onClick={onClose}>X</button>
                </div>

                <form onSubmit={handleSubmit} className="p-4 space-y-4">
                    {note && (
                        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 text-sm p-2 rounded">
                            {note}
                        </div>
                    )}
                    <div>
                        <label className="block text-sm font-medium mb-2">Selected Controls</label>
                        <div className="border rounded p-2 min-h-14 max-h-32 overflow-y-auto">
                            {selectedControls.length === 0 ? (
                                <p className="text-sm text-gray-500">No controls selected</p>
                            ) : (
                                <div className="flex flex-wrap gap-2">
                                    {selectedControls.map((control) => (
                                        <span
                                            key={control.id}
                                            className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-full px-2 py-1 text-xs"
                                        >
                                            <span className="font-medium">{control.control_id}</span>
                                            <button
                                                type="button"
                                                onClick={() => onRemoveControl(control.id)}
                                                className="text-blue-400 hover:text-red-500"
                                                aria-label={`Remove ${control.control_id}`}
                                            >
                                                X
                                            </button>
                                        </span>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Due Date</label>
                        <input
                            type="date"
                            value={dueDate}
                            onChange={(e) => setDueDate(e.target.value)}
                            min={new Date().toISOString().split("T")[0]}
                            className="w-full p-2 border rounded"
                            required
                        />
                    </div>

                    <div>
                        <label className="block text-sm font-medium mb-1">Assign To</label>
                        <div className="relative">
                            <div className="relative">
                                <input
                                    type="text"
                                    placeholder="Search employee..."
                                    value={searchTerm}
                                    onChange={(e) => {
                                        setSearchTerm(e.target.value);
                                        setIsDropdownOpen(true);
                                        if (selectedEmployee && e.target.value === "") {
                                            setSelectedEmployee(null);
                                        }
                                    }}
                                    onFocus={() => setIsDropdownOpen(true)}
                                    className="w-full p-2 border rounded pr-8 focus:outline-none focus:ring-2 focus:ring-blue-300"
                                />

                                {(selectedEmployee || searchTerm) && (
                                    <button
                                        type="button"
                                        onClick={clearSelection}
                                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                        X
                                    </button>
                                )}
                            </div>

                            {isDropdownOpen && (
                                <div className="absolute z-10 w-full mt-1 border rounded bg-white shadow-lg max-h-60 overflow-y-auto">
                                    {searching ? (
                                        <div className="p-2 text-gray-500">Searching...</div>
                                    ) : employeeOptions.length > 0 ? (
                                        employeeOptions.map((emp) => (
                                            <div
                                                key={emp.employee_id}
                                                onClick={() => handleSelectEmployee(emp)}
                                                className={`p-2 hover:bg-gray-100 cursor-pointer ${selectedEmployee?.user_id === emp.user_id
                                                    ? "bg-blue-50 font-medium"
                                                    : ""
                                                    }`}
                                            >
                                                {`${emp.firstname || ""} ${emp.lastname || ""}`.trim()}
                                            </div>
                                        ))
                                    ) : searchTerm ? (
                                        <div className="p-2 text-gray-500">No employees found</div>
                                    ) : (
                                        <div className="p-2 text-gray-500">Type to search...</div>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="flex justify-end space-x-2 pt-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-4 py-2 border rounded hover:bg-gray-50"
                            disabled={loading}
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={loading || selectedControls.length === 0 || dueDate === "" || !selectedEmployee}
                            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? "Assigning..." : "Assign"}
                        </button>
                    </div>


                </form>
            </div>
        </div>
    );
};

export default AssignSelectedControlsModal;